/**
 * The automation tab's pure half: how the status reply becomes the lines the
 * cards say (today's calling window, today's counts, each campaign's verdict,
 * each tick in the history) and how its moments are written.
 *
 * Its own file beside the cards rather than more of `lib/aiAutomation.ts`,
 * which is the mirror of the server's route and its word lists. What is here
 * only reads that mirror, so it can change with the page without touching the
 * file a server change has to be checked against.
 *
 * Times are written in the automation's own zone (`settings.timezone`), not the
 * browser's: the window the operator set ("10:00-16:00") is in that zone, and a
 * "next opening at 10:00" printed in another zone would contradict the line
 * right above it. The campaign editor's date pickers work in the browser's
 * zone, so when the two differ each moment here names its zone, and the editor
 * says which zone its pickers use (`zoneLabel`). React-free, so it runs under
 * plain node.
 */
import {
  AUTO_REASONS,
  TICK_OUTCOMES,
  TICK_TRIGGERS,
  WINDOW_REASONS,
  campaignSkipKey,
  outcomeTone,
  tickReasonKey,
  type AutomationCampaign,
  type AutomationDecision,
  type AutomationStatus,
  type AutomationWindow,
  type StopReply,
  type TickReport,
} from "../../../../lib/aiAutomation";
import type { BusinessDay } from "../../../../lib/telephonyHours";
import type { StatusTone } from "../../../ui";

// ---------------------------------------------------------------- time

/** A stored minute count as a clock face: 600 reads 10:00. */
export function clock(minutes: number): string {
  const m = Math.max(0, Math.min(24 * 60, Math.trunc(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** The zone when this runtime knows it, else undefined (the browser's own). */
function knownZone(timeZone: string): string | undefined {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat("en", { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
}

/** The calendar date of an instant in a zone, "YYYY-MM-DD". */
function dateIn(at: Date, timeZone: string | undefined): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The zone this browser writes dates in (the date pickers use it), or "" when it cannot say. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}

/**
 * The automation's zone, when a moment written in it needs its name: the viewer
 * is in another zone. Null when both are the same, or either is unknown.
 */
export function zoneLabel(timeZone: string, viewerZone: string): string | null {
  const zone = knownZone(timeZone);
  const viewer = knownZone(viewerZone);
  if (!zone || !viewer) return null;
  const canonical = (z: string) => new Intl.DateTimeFormat("en", { timeZone: z }).resolvedOptions().timeZone;
  return canonical(zone) === canonical(viewer) ? null : zone;
}

/**
 * A moment as short as it can be said: the clock time when it falls on the
 * same local day as `now`, otherwise a short date with the time. "" for a
 * missing or unreadable instant. With `viewerZone` (the browser's) in another
 * zone than `timeZone`, the zone's name follows, so it is never read as local.
 */
export function fmtWhen(iso: string | null, now: string, locale: string, timeZone: string, viewerZone?: string): string {
  if (!iso) return "";
  const at = new Date(iso);
  const today = new Date(now);
  if (Number.isNaN(at.getTime())) return "";
  const zone = knownZone(timeZone);
  const time: Intl.DateTimeFormatOptions = { timeZone: zone, hour: "2-digit", minute: "2-digit" };
  const text =
    !Number.isNaN(today.getTime()) && dateIn(at, zone) === dateIn(today, zone)
      ? new Intl.DateTimeFormat(locale, time).format(at)
      : new Intl.DateTimeFormat(locale, { ...time, day: "numeric", month: "short" }).format(at);
  const label = viewerZone === undefined ? null : zoneLabel(timeZone, viewerZone);
  return label ? `${text} (${label})` : text;
}

// ---------------------------------------------------------------- window

/**
 * Today's calling window in the zone, from the reply's local date. Null when
 * the zone is unknown ("" date) or the day is closed, which the server also
 * reads for a day whose end is not after its start.
 */
export function windowToday(days: readonly BusinessDay[], localDate: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!m) return null;
  const weekday = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  const day = days[weekday];
  if (!day || !day.open || day.to <= day.from) return null;
  return { from: clock(day.from), to: clock(day.to) };
}

const WINDOWS: ReadonlySet<string> = new Set<string>(WINDOW_REASONS);

/** Where the window stands as a key; an unknown reason as `window.unknown`. */
export function windowKey(window: Pick<AutomationWindow, "reason">): string {
  return `aiCalls.automation.window.${WINDOWS.has(window.reason) ? window.reason : "unknown"}`;
}

// ---------------------------------------------------------------- decision

const REASONS: ReadonlySet<string> = new Set<string>(AUTO_REASONS);

/** The decision's headline key (`reason.<r>` or `reason.unknown`). */
export function decisionKey(decision: Pick<AutomationDecision, "reason">): string {
  return `aiCalls.automation.reason.${REASONS.has(decision.reason) ? decision.reason : "unknown"}`;
}

/**
 * What `until` means for this reason: the next retry of an empty queue, or the
 * moment the automation may go on (window opens, next day, gap over). Null
 * when there is no time to say.
 */
export function untilKey(decision: Pick<AutomationDecision, "reason" | "until">): string | null {
  if (!decision.until) return null;
  return decision.reason === "no_targets" ? "aiCalls.automation.decision.untilRetry" : "aiCalls.automation.decision.until";
}

export interface TodayCount {
  mode: "real" | "test";
  used: number;
  cap: number;
  /** The mode the automation calls in now. */
  active: boolean;
  /** Today's cap is spent. */
  full: boolean;
}

/** Today's calls against their caps, the active mode first. */
export function todayCounts(status: Pick<AutomationStatus, "today" | "settings">): TodayCount[] {
  const { today, settings } = status;
  const real: TodayCount = { mode: "real", used: today.real, cap: today.realCap, active: !settings.testMode, full: today.realCap > 0 && today.real >= today.realCap };
  const test: TodayCount = { mode: "test", used: today.test, cap: today.testCap, active: settings.testMode, full: today.testCap > 0 && today.test >= today.testCap };
  return settings.testMode ? [test, real] : [real, test];
}

// ---------------------------------------------------------------- campaigns

export interface CampaignLine {
  /** The verdict's key: the next call's source, a skip, or ready. */
  key: string;
  tone: StatusTone;
  /** The decision will ring from this campaign next. */
  next: boolean;
}

/**
 * One campaign's verdict in words. A campaign with no skip is eligible: it is
 * "next" when the decision picked it, otherwise "ready" (the automation waits
 * for something global, or picked another one).
 */
export function campaignLine(campaign: Pick<AutomationCampaign, "id" | "skip">, decision: Pick<AutomationDecision, "go" | "campaignId">): CampaignLine {
  if (decision.go && decision.campaignId === campaign.id) return { key: "aiCalls.automation.campaigns.next", tone: "ok", next: true };
  if (!campaign.skip) return { key: "aiCalls.automation.campaigns.ready", tone: "info", next: false };
  const quiet = campaign.skip === "not_active" || campaign.skip === "auto_off";
  return { key: campaignSkipKey(campaign.skip), tone: quiet ? "mut" : "wt", next: false };
}

/** "Call the next one now" is offered on a campaign the server would consider at all. */
export function offersCallNext(campaign: Pick<AutomationCampaign, "status">, canManage: boolean): boolean {
  return canManage && campaign.status === "active";
}

// ---------------------------------------------------------------- history

const OUTCOMES: ReadonlySet<string> = new Set<string>(TICK_OUTCOMES);
const TRIGGERS: ReadonlySet<string> = new Set<string>(TICK_TRIGGERS);

export interface TickFacts {
  outcomeKey: string;
  tone: StatusTone;
  reasonKey: string;
  /** Pressed by somebody rather than run by the schedule. */
  manual: boolean;
  triggerKey: string;
  company: string | null;
  campaignName: string | null;
  test: boolean;
  skipped: number;
  added: number;
  tasks: number;
  /** How many times this line happened in a row (1 for a single tick). */
  times: number;
}

/** One history line from a tick report; a campaign's name from the reply's campaign list. */
export function tickFacts(report: TickReport, campaigns: readonly Pick<AutomationCampaign, "id" | "name">[]): TickFacts {
  const campaign = report.campaignId ? campaigns.find((c) => c.id === report.campaignId) : undefined;
  return {
    outcomeKey: `aiCalls.automation.history.outcome.${OUTCOMES.has(report.outcome) ? report.outcome : "unknown"}`,
    tone: outcomeTone(report.outcome),
    // A placed call has nothing to explain; its line names the company instead.
    reasonKey: report.outcome === "placed" ? "" : tickReasonKey(report),
    manual: report.trigger === "call_now",
    triggerKey: `aiCalls.automation.history.trigger.${TRIGGERS.has(report.trigger) ? report.trigger : "schedule"}`,
    company: report.companyName,
    campaignName: campaign?.name || null,
    test: report.test,
    skipped: report.skipped.length,
    added: report.refilled.reduce((sum, r) => sum + r.added, 0),
    tasks: report.clientTasks,
    times: report.repeats + 1,
  };
}

// ---------------------------------------------------------------- stop

/** The lines the stop note adds after "stopped", each a plural key with its count. Zero counts are left out. */
export function stopLines(reply: StopReply): Array<{ key: string; n: number }> {
  const lines: Array<{ key: string; n: number }> = [];
  if (reply.ended > 0) lines.push({ key: "aiCalls.automation.controls.stoppedEnded", n: reply.ended });
  if (reply.pending > 0) lines.push({ key: "aiCalls.automation.controls.stoppedPending", n: reply.pending });
  if (reply.failed > 0) lines.push({ key: "aiCalls.automation.controls.stoppedFailed", n: reply.failed });
  return lines;
}
